import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    UpdateProduct as UpdateProductMutation,
    UpdateProductVariables as UpdateProductVariables,
    ProductUpdateInput,
} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../../../util/bust-managed-collection-cache';
import GET_PRODUCT_CATEGORIES from '../../../../categories/graphql/queries/get-product-categories.graphql';
import GET_PRODUCTS_SIDEBAR_COUNTS from '../../../../categories/graphql/queries/get-products-sidebar-counts.graphql';

import UPDATE_PRODUCT from '../mutations/update-product.graphql';
import GET_PRODUCTS from '../queries/get-products.graphql';

import {GET_PRODUCTS_LIMIT} from './use-get-products';

export const useUpdateProduct = (
    selectedCategoryId?: string | null,
    archived?: boolean | null
): {
    updateProduct: (arg1: ProductUpdateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateProduct, {loading, error}] = useMutation<
        UpdateProductMutation,
        UpdateProductVariables
    >(UPDATE_PRODUCT);

    return {
        updateProduct: (input: ProductUpdateInput) =>
            updateProduct({
                variables: {input},
                refetchQueries: [
                    {query: GET_PRODUCTS_SIDEBAR_COUNTS},
                    {query: GET_PRODUCT_CATEGORIES},
                ],
                update: (proxy, {data}) => {
                    const product = data && data.productUpdate && data.productUpdate.product;

                    if (!product || !selectedCategoryId) return;

                    const products = proxy.readQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                    });

                    const productCategoryId = product.category && product.category.id;

                    // We only want to manually set new edges when we are moving
                    // from one category to another from a given category list
                    if (!products || productCategoryId === selectedCategoryId) return;

                    // @ts-expect-error
                    const newEdges = products.products.edges.filter(
                        // @ts-expect-error
                        (edge) => edge.node.category.id !== productCategoryId
                    );

                    proxy.writeQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                        data: {
                            products: {
                                edges: newEdges,
                                __typename: 'ProductsConnection',
                            },
                            __typename: 'Query',
                        },
                    });
                },
            }).then(() => {
                // Ensure REST-backed products lists refresh to include product changes
                bustManagedCollectionCache('products');
                bustManagedCollectionCache('productCategories');
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
